import React from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';

import { invokeAgentWithResults, AgentExplanation as AgentExplanationType } from '../api/ExplainabilityApi';
import { ErrorInfo, formatTimestamp, getDirectionIcon, getErrorMessage } from '../utils/agentExplanationUtils';

export interface AgentExplanationInlineProps {
  results: unknown[];
  metadata: Record<string, string>;
}

type ComponentState = 'idle' | 'loading' | 'success' | 'error';

const AGENT_TIMEOUT_MS = 30_000;

export default function AgentExplanationInline({ results, metadata }: AgentExplanationInlineProps): JSX.Element {
  const [state, setState] = React.useState<ComponentState>('idle');
  const [explanation, setExplanation] = React.useState<AgentExplanationType | null>(null);
  const [errorInfo, setErrorInfo] = React.useState<ErrorInfo | null>(null);

  const abortRef = React.useRef<AbortController | null>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset to idle when results change
  React.useEffect(() => {
    setState('idle');
    setExplanation(null);
    setErrorInfo(null);
  }, [results]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInvoke = async () => {
    // Abort any previous request
    abortRef.current?.abort();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setState('loading');
    setErrorInfo(null);
    setExplanation(null);

    // Set up 30s timeout
    timeoutRef.current = setTimeout(() => {
      controller.abort();
    }, AGENT_TIMEOUT_MS);

    try {
      const result = await invokeAgentWithResults(results, metadata, controller.signal);

      if (controller.signal.aborted) return;

      setExplanation(result);
      setState('success');
    } catch (err) {
      if (controller.signal.aborted && state === 'loading') {
        // Timeout triggered the abort
        setErrorInfo({ message: 'Explanation timed out. Please retry.', status: 504 });
        setState('error');
        return;
      }
      setErrorInfo(getErrorMessage(err));
      setState('error');
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  const handleRetry = () => {
    handleInvoke();
  };

  // Idle state: show button
  if (state === 'idle') {
    return (
      <Container
        header={<Header variant="h3">🤖 AI Explanation</Header>}
      >
        <Button
          iconName="gen-ai"
          onClick={handleInvoke}
          aria-label="Explain attribution results with AI"
        >
          Explain with AI
        </Button>
      </Container>
    );
  }

  // Loading state
  if (state === 'loading') {
    return (
      <Container
        header={<Header variant="h3">🤖 AI Explanation</Header>}
      >
        <div aria-live="polite">
          <SpaceBetween size="s" alignItems="center" direction="horizontal">
            <StatusIndicator type="loading">
              Generating explanation…
            </StatusIndicator>
          </SpaceBetween>
        </div>
      </Container>
    );
  }

  // Error state
  if (state === 'error' && errorInfo) {
    return (
      <Container
        header={<Header variant="h3">🤖 AI Explanation</Header>}
      >
        <div aria-live="polite">
          <SpaceBetween size="s">
            <Alert type="error">{errorInfo.message}</Alert>
            <Button onClick={handleRetry} iconName="refresh">
              Retry
            </Button>
          </SpaceBetween>
        </div>
      </Container>
    );
  }

  // Success state
  if (state === 'success' && explanation) {
    return (
      <Container
        header={
          <Header
            variant="h3"
            description={`Generated at ${formatTimestamp(explanation.generated_at)}`}
          >
            🤖 AI Explanation
          </Header>
        }
      >
        <div aria-live="polite">
          <SpaceBetween size="l">
            {/* Summary */}
            <div>
              <Box variant="h4">Summary</Box>
              <Box variant="p">{explanation.summary}</Box>
            </div>

            {/* Per-view breakdown table (divergence view) */}
            {explanation.view_breakdown && explanation.view_breakdown.length > 0 && (
              <div>
                <Box variant="h4">Per-view breakdown</Box>
                <Table
                  variant="embedded"
                  stripedRows
                  columnDefinitions={[
                    { id: 'view', header: 'View', cell: (i) => i.view },
                    { id: 'quantile', header: 'Quantile', cell: (i) => i.quantile },
                    { id: 'first', header: 'First', cell: (i) => i.first.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                    { id: 'last', header: 'Last', cell: (i) => i.last.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                    { id: 'change', header: 'Change', cell: (i) => `${i.change >= 0 ? '+' : ''}${i.change.toLocaleString(undefined, { maximumFractionDigits: 2 })} (${i.change_pct >= 0 ? '+' : ''}${i.change_pct}%)` },
                    { id: 'min', header: 'Min', cell: (i) => i.min.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                    { id: 'max', header: 'Max', cell: (i) => i.max.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                    { id: 'note', header: 'Note', cell: (i) => (
                      <Badge color={i.note === 'BREACH' ? 'red' : i.note === 'up' ? 'green' : i.note === 'down' ? 'blue' : 'grey'}>{i.note}</Badge>
                    ) },
                  ]}
                  items={explanation.view_breakdown}
                />
              </div>
            )}

            {/* Top Contributing Features (hidden when the agent returns no features, e.g. divergence view) */}
            {explanation.top_features.length > 0 && (
            <div>
              <Box variant="h4">Top Contributing Features</Box>
              <Table
                columnDefinitions={[
                  {
                    id: 'name',
                    header: 'Feature',
                    cell: (item) => item.name,
                  },
                  {
                    id: 'score',
                    header: 'Score',
                    cell: (item) => item.score.toFixed(2),
                  },
                  {
                    id: 'direction',
                    header: 'Direction',
                    cell: (item) => (
                      <SpaceBetween direction="horizontal" size="xs">
                        <span>{getDirectionIcon(item.direction)}</span>
                        <Badge
                          color={
                            item.direction.toLowerCase() === 'positive' ||
                            item.direction.toLowerCase() === 'up'
                              ? 'green'
                              : item.direction.toLowerCase() === 'negative' ||
                                item.direction.toLowerCase() === 'down'
                              ? 'red'
                              : 'grey'
                          }
                        >
                          {item.direction}
                        </Badge>
                      </SpaceBetween>
                    ),
                  },
                  {
                    id: 'explanation',
                    header: 'Explanation',
                    cell: (item) => item.explanation,
                  },
                ]}
                items={explanation.top_features}
                variant="embedded"
                stripedRows
              />
            </div>
            )}

            {/* Suggested Next Steps */}
            <div>
              <Box variant="h4">Suggested Next Steps</Box>
              <ul>
                {explanation.next_steps.map((step, index) => (
                  <li key={index}>
                    <Box variant="p">{step}</Box>
                  </li>
                ))}
              </ul>
            </div>
          </SpaceBetween>
        </div>
      </Container>
    );
  }

  // Fallback (should not reach here)
  return <></>;
}
